/**
 * O que faz: transforma recusa da Meta/CRM em texto claro para quem opera o CRM.
 * Onde usa: toast do envio de teste e interpretação do corpo da Edge Function.
 * Conexões: mesmo critério de supabase/functions/_shared/mensagem-erro-envio-oficial.ts.
 * Data: 09/09/2026
 */

export type TipoErroEnvioOficial =
  | 'pagamento'
  | 'permissao'
  | 'template'
  | 'contato'
  | 'limite'
  | 'configuracao'
  | 'sistema'

export interface MensagemErroEnvioOficial {
  tipo: TipoErroEnvioOficial
  mensagem: string
  acao?: string
}

export class ErroEnvioWhatsAppOficial extends Error {
  readonly tipo: TipoErroEnvioOficial
  readonly acao?: string

  constructor(resposta: MensagemErroEnvioOficial) {
    super(resposta.mensagem)
    this.name = 'ErroEnvioWhatsAppOficial'
    this.tipo = resposta.tipo
    this.acao = resposta.acao
  }
}

function textoDeValor(valor: unknown): string {
  if (typeof valor === 'string') return valor
  if (typeof valor === 'number' && Number.isFinite(valor)) return String(valor)
  return ''
}

function coletarTextos(valor: unknown, acc: string[]): void {
  if (valor == null) return
  if (typeof valor === 'string' || typeof valor === 'number') {
    const texto = textoDeValor(valor).trim()
    if (texto) acc.push(texto)
    return
  }
  if (Array.isArray(valor)) {
    for (const item of valor) coletarTextos(item, acc)
    return
  }
  if (typeof valor !== 'object') return
  const registro = valor as Record<string, unknown>
  for (const chave of ['message', 'error_user_msg', 'error_user_title', 'details', 'title', 'error']) {
    if (chave === 'error' && typeof registro.error === 'object') {
      coletarTextos(registro.error, acc)
      continue
    }
    coletarTextos(registro[chave], acc)
  }
}

function coletarCodigos(valor: unknown, acc: number[]): void {
  if (valor == null) return
  if (Array.isArray(valor)) {
    for (const item of valor) coletarCodigos(item, acc)
    return
  }
  if (typeof valor !== 'object') return
  const registro = valor as Record<string, unknown>
  for (const chave of ['code', 'error_subcode', 'error_code']) {
    const numero = Number(registro[chave])
    if (Number.isFinite(numero)) acc.push(numero)
  }
  const mensagem = textoDeValor(registro.message)
  const codigoNaMensagem = mensagem.match(/#(\d+)/)
  if (codigoNaMensagem) acc.push(Number(codigoNaMensagem[1]))
  if (registro.error) coletarCodigos(registro.error, acc)
  if (registro.error_data) coletarCodigos(registro.error_data, acc)
}

function textoNormalizado(valor: unknown): string {
  const partes: string[] = []
  coletarTextos(valor, partes)
  return partes.join(' ').toLowerCase()
}

const CODIGOS_PAGAMENTO = new Set([131042, 2388103])
const CODIGOS_PERMISSAO = new Set([10, 190, 200, 368])
const CODIGOS_CONTA_BLOQUEADA = new Set([131031])
const CODIGOS_TEMPLATE = new Set([132000, 132001, 132005, 132007, 132012, 132015, 132016])
const CODIGOS_CONTATO = new Set([131026, 131047, 131049, 131050, 130472])
const CODIGOS_LIMITE = new Set([130429, 131048, 131056])
const CODIGOS_NUMERO = new Set([131045, 133010])

export function interpretarErroEnvioOficial(origem: unknown): MensagemErroEnvioOficial {
  if (origem && typeof origem === 'object' && 'tipo' in origem && 'mensagem' in origem) {
    const candidato = origem as MensagemErroEnvioOficial
    if (typeof candidato.mensagem === 'string' && candidato.mensagem.trim()) {
      return {
        tipo: candidato.tipo,
        mensagem: candidato.mensagem,
        acao: typeof candidato.acao === 'string' ? candidato.acao : undefined,
      }
    }
  }

  if (typeof origem === 'string') {
    return interpretarErroEnvioOficial({ message: origem })
  }
  if (origem instanceof Error) {
    return interpretarErroEnvioOficial({ message: origem.message, error: origem.message })
  }

  const registro = origem && typeof origem === 'object'
    ? origem as Record<string, unknown>
    : {}
  if (typeof registro.error === 'string' && typeof registro.tipo === 'string') {
    return {
      tipo: registro.tipo as TipoErroEnvioOficial,
      mensagem: registro.error,
      acao: typeof registro.acao === 'string' ? registro.acao : undefined,
    }
  }

  const textoCrm = typeof registro.error === 'string' ? registro.error : ''
  if (textoCrm.includes('não está ativa')) {
    return {
      tipo: 'configuracao',
      mensagem: 'O WhatsApp oficial desta clínica está desligado.',
      acao: 'Em Disparos, abra a configuração e ligue a API oficial. Depois salve e tente de novo.',
    }
  }
  if (textoCrm.includes('ainda não está aprovado')) {
    return {
      tipo: 'template',
      mensagem: 'Este modelo ainda não foi aprovado pela Meta.',
      acao: 'Espere a aprovação ou escolha outro modelo com status aprovado.',
    }
  }
  if (textoCrm.includes('não receber mensagens')) {
    return {
      tipo: 'contato',
      mensagem: 'Este contato pediu para não receber divulgação.',
      acao: 'Use outro telefone ou um modelo que não seja de marketing.',
    }
  }
  if (textoCrm.includes('telefone válido')) {
    return {
      tipo: 'contato',
      mensagem: 'O telefone precisa ter DDD.',
      acao: 'Digite o número com DDD, por exemplo 11 99999-9999.',
    }
  }

  const textos = textoNormalizado(origem)
  const codigos: number[] = []
  coletarCodigos(origem, codigos)

  if (codigos.some((codigo) => CODIGOS_PAGAMENTO.has(codigo)) || /payment|billing|invoice|unpaid|payment method|fatura|cart[aã]o/.test(textos)) {
    return {
      tipo: 'pagamento',
      mensagem: 'A Meta bloqueou o envio por causa do pagamento desta conta WhatsApp.',
      acao: 'No Gerenciador de Negócios, abra Pagamentos da conta WhatsApp. Confira se o cartão está válido e se existe fatura em aberto.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_PERMISSAO.has(codigo)) || textos.includes('necessary permissions') || textos.includes('does not have permission')) {
    return {
      tipo: 'permissao',
      mensagem: 'Esta conta WhatsApp ainda não está autorizada a enviar pelo aplicativo ligado no CRM.',
      acao: 'Quem administra a Meta precisa conferir o acesso do número a este aplicativo e gerar um token novo. Depois cole o token na configuração e salve.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_CONTA_BLOQUEADA.has(codigo))) {
    return {
      tipo: 'permissao',
      mensagem: 'A Meta restringiu esta conta WhatsApp.',
      acao: 'Abra o WhatsApp Manager e veja se a conta pede alguma confirmação. Isso não se resolve dentro do CRM.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_TEMPLATE.has(codigo))) {
    return {
      tipo: 'template',
      mensagem: 'O modelo não pôde ser enviado neste formato.',
      acao: 'Confira se o modelo está aprovado e se todos os campos {{1}}, {{2}} e {{3}} foram preenchidos.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_CONTATO.has(codigo))) {
    return {
      tipo: 'contato',
      mensagem: 'A Meta não entregou para este número.',
      acao: 'Confira se o WhatsApp do destino está ativo. Se for divulgação, a pessoa pode ter recusado este tipo de mensagem.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_LIMITE.has(codigo))) {
    return {
      tipo: 'limite',
      mensagem: 'A Meta pediu para esperar: o limite de envio desta conta foi atingido.',
      acao: 'Aguarde alguns minutos e tente de novo, com menos envios seguidos.',
    }
  }
  if (codigos.some((codigo) => CODIGOS_NUMERO.has(codigo))) {
    return {
      tipo: 'configuracao',
      mensagem: 'O número oficial ainda não está pronto para enviar.',
      acao: 'Confira no WhatsApp Manager se o número está registrado e com nome de exibição aprovado.',
    }
  }

  const mensagemSupabase = textos.includes('failed to send a request to the edge function')
    || textos.includes('failed to send a request')
  if (mensagemSupabase || textos.includes('erro interno')) {
    return {
      tipo: 'sistema',
      mensagem: 'Não foi possível enviar agora.',
      acao: 'Tente de novo em alguns minutos. Se continuar falhando, fale com o suporte.',
    }
  }

  return {
    tipo: 'sistema',
    mensagem: 'Não foi possível enviar agora.',
    acao: 'Tente de novo em alguns minutos. Se continuar falhando, fale com o suporte.',
  }
}
